import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { PackagePlus, Search, Loader2, Link2, X } from 'lucide-react';
import { API } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';

const UNIT_MAP = { pcs: 'NOS', nos: 'NOS', set: 'SET', litres: 'LTR', kg: 'KGS', 'sq ft': 'SQF', metres: 'MTR' };
const r2 = (v) => Math.round(v * 100) / 100;
const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// One bill line from a part. Selling price includes GST, so taxed bills get
// the pre-tax rate; a plain invoice charges the selling price as-is.
export const partLine = (part, { taxed, split }) => {
  const gst = Number(part.gst_rate ?? 0);
  const price = Number(part.selling_price || 0);
  const label = [part.brand, part.sku && `Part no. ${part.sku}`].filter(Boolean).join(' · ');
  return {
    part_id: part.id,
    qty: 1,
    unit: UNIT_MAP[(part.unit || '').toLowerCase()] || 'NOS',
    hsn_code: part.hsn_code || '',
    gst_rate: taxed ? gst : '',
    rate: taxed ? r2(price / (1 + gst / 100)) : price,
    // Lock the incl-GST total to the selling price so paise rounding never
    // bills ₹2,200.01 for a ₹2,200 part.
    line_total_target: taxed && price > 0 ? price : '',
    _stock: { qty: Number(part.quantity || 0), unit: part.unit, sku: part.sku },
    ...(split
      ? { name: part.name, description: label }
      : { description: label ? `${part.name} (${label})` : part.name }),
  };
};

// A part line's locked total follows its quantity, so 2 × ₹2,200 is ₹4,400.
export const withPartQty = (item, patch) => {
  if (!('qty' in patch) || !item.part_id) return patch;
  const oldQty = Number(item.qty) || 0;
  const newQty = Number(patch.qty) || 0;
  const target = Number(item.line_total_target) || 0;
  if (!target || !oldQty || !newQty) return patch;
  return { ...patch, line_total_target: r2((target / oldQty) * newQty) };
};

export const PartChip = ({ item, index, onUnlink }) => {
  if (!item.part_id) return null;
  const stock = item._stock;
  const short = stock && Number(item.qty || 0) > stock.qty;
  return (
    <div className={`mt-1 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] ${short ? 'border-red-200 bg-red-50 text-red-700' : 'border-teal-200 bg-teal-50 text-teal-800'}`} data-testid={`part-chip-${index}`}>
      <Link2 size={11} />
      {stock ? `${short ? 'Only ' : ''}${fmt(stock.qty)} ${stock.unit || ''} in stock` : 'From inventory'}
      <button type="button" onClick={onUnlink} className="ml-0.5 hover:text-red-600" aria-label="Stop taking this line from stock" title="Stop taking this line from stock" data-testid={`part-unlink-${index}`}>
        <X size={11} />
      </button>
    </div>
  );
};

const PartRow = ({ part, onPick }) => {
  const out = Number(part.quantity || 0) <= 0;
  return (
    <button type="button" onClick={() => onPick(part)} className="flex w-full items-start justify-between gap-3 px-3 py-2 text-left hover:bg-teal-50 transition-colors" data-testid={`part-option-${part.id}`}>
      <div className="min-w-0">
        <div className="truncate text-sm font-medium text-slate-900">{part.name}</div>
        <div className="truncate text-[11px] text-slate-500">
          {[part.sku, part.brand, part.hsn_code && `HSN ${part.hsn_code}`].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm font-semibold tabular-nums text-slate-900">₹ {fmt(part.selling_price)}</div>
        <div className={`text-[11px] ${out ? 'text-red-600' : 'text-slate-500'}`}>{out ? 'Out of stock' : `${fmt(part.quantity)} ${part.unit} left`}</div>
      </div>
    </button>
  );
};

export const PartPicker = ({ onPick, testid = 'add-part-btn' }) => {
  const { authHeader } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/inventory/saleable`, { params: { q }, headers: authHeader() });
        setParts(data || []);
      } catch { setParts([]); } finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(t);
  }, [open, q, authHeader]);

  const pick = (part) => { onPick(part); setOpen(false); setQ(''); };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1.5 text-xs btn-ink px-3 py-1.5 rounded-lg transition-colors" data-testid={testid}>
        <PackagePlus size={13} /> Add part
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-[22rem] max-w-[calc(100vw-2rem)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl" data-testid="part-picker">
          <div className="relative border-b border-slate-200">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, part no., brand or HSN" className="w-full bg-transparent py-2.5 pl-9 pr-3 text-sm text-slate-900 outline-none" data-testid="part-search" />
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100">
            {loading && <div className="flex items-center gap-2 px-3 py-4 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" /> Looking…</div>}
            {!loading && parts.length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-500" data-testid="part-picker-empty">
                No parts for sale yet. In Inventory, mark an item &ldquo;For sale&rdquo; and give it a selling price.
              </div>
            )}
            {!loading && parts.map((p) => <PartRow key={p.id} part={p} onPick={pick} />)}
          </div>
        </div>
      )}
    </div>
  );
};

export default PartPicker;
